import { useState } from "react";
import { Button } from "src/vibe-code/rcm-v2/components/ui/button";
import { Input } from "src/vibe-code/rcm-v2/components/ui/input";
import { Label } from "src/vibe-code/rcm-v2/components/ui/label";
import { Textarea } from "src/vibe-code/rcm-v2/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/vibe-code/rcm-v2/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/vibe-code/rcm-v2/components/ui/card";
import { Separator } from "src/vibe-code/rcm-v2/components/ui/separator";
import { MultiSelect } from "src/vibe-code/rcm-v2/components/ui/multi-select";
import { Checkbox } from "src/vibe-code/rcm-v2/components/ui/checkbox";
import { parseIsoDate } from "src/utils";
import type { Dealer } from "../../types";
import { getDealerTypeOptions } from "../../types";
import { normalizeUrl } from "src/utils/index";

interface DealerFormProps {
  dealer?: Dealer;
  onSave: (dealer: Partial<Dealer>) => void;
  onCancel: () => void;
}

export function DealerForm({ dealer, onSave, onCancel }: DealerFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressInfo, setAddressInfo] = useState({
    street1: dealer?.street1 || "",
    street2: dealer?.street2 || "",
    city: dealer?.city || "",
    state: dealer?.state || "",
    zip_code: dealer?.zip_code || "",
  });

  const formatDateForForm = (isoDate: string) => {
    if (!isoDate) return "";
    const { year, month, day } = parseIsoDate(isoDate);
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  };

  const [formData, setFormData] = useState({
    name: dealer?.name || "",
    store_number: dealer?.store_number || "",
    external_id: dealer?.id || "",
    website_url: dealer?.website_url || "",
    phone_number: dealer?.phone_number || "",
    email: dealer?.email || "",
    dealer_type: dealer?.dealer_type || ("INDEPENDENT" as const),
    lenders: dealer?.lenders || [],
    uses_dla: dealer?.uses_dla || false,
    onboarding_date: formatDateForForm(dealer?.onboarding_date || ""),
    last_visit_date: formatDateForForm(dealer?.last_visit_date || ""),

    vep_code: dealer?.vep_code || "",
    dealer_feedback: dealer?.dealer_feedback || "",
  });
  const presetLenders = [
    "PorchPass",
    "21st Mortgage",
    "Triad",
    "Cascade",
    "CSL",
    "FirstBank",
    "Vanderbilt",
    "CountryPlace",
    "autoMHatic",
    "Right Start Mortgage",
    "Cardinal",
    "Home Funding Corp",
  ];

  const handleInputChange = (field: string, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleLendersChange = (lenders: string[]) => {
    setFormData((prev) => ({ ...prev, lenders }));
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    setFormData((prev) => ({ ...prev, [field]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare dealer data
      const dealerData = {
        ...formData,
        ...addressInfo,
        website_url: normalizeUrl(formData.website_url),
      };

      // Save the dealer data
      onSave(dealerData);
    } catch (error) {
      console.error("Error in form submission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card className="shadow-custom-lg">
        <CardHeader>
          <CardTitle className="text-2xl">
            {dealer ? "Edit Dealer" : "Add New Dealer"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Dealer Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter dealer name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="store_number">Store Number</Label>
                <Input
                  id="store_number"
                  value={formData.store_number}
                  onChange={(e) =>
                    handleInputChange("store_number", e.target.value)
                  }
                  placeholder="e.g., TC001"
                />
              </div>
              <div>
                <Label htmlFor="external_id">External ID *</Label>
                <Input
                  id="external_id"
                  value={formData.external_id}
                  onChange={(e) =>
                    handleInputChange("external_id", e.target.value)
                  }
                  placeholder="e.g., palm-harbor-houston"
                  required
                  readOnly={!!dealer}
                  className={dealer ? "bg-muted cursor-not-allowed" : ""}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  This is the Dealer's DLA URL link. For multiple locations use
                  "dealername-location" format. For one location, just use the
                  dealership's name.
                  <br />
                  <span className="text-xs italic">
                    Example: app.porchpass.com/palm-harbor-houston
                  </span>
                </p>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="street1">Address *</Label>
                <Input
                  id="street1"
                  value={addressInfo.street1}
                  onChange={(e) =>
                    setAddressInfo((prev) => ({
                      ...prev,
                      street1: e.target.value,
                    }))
                  }
                  placeholder="Street address"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="street2">Address 2</Label>
                <Input
                  id="street2"
                  value={addressInfo.street2}
                  onChange={(e) =>
                    setAddressInfo((prev) => ({
                      ...prev,
                      street2: e.target.value,
                    }))
                  }
                  placeholder="Apartment, suite, etc. (optional)"
                />
              </div>
              <div>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  value={addressInfo.city}
                  onChange={(e) =>
                    setAddressInfo((prev) => ({
                      ...prev,
                      city: e.target.value,
                    }))
                  }
                  placeholder="City"
                  required
                />
              </div>
              <div>
                <Label htmlFor="state">State *</Label>
                <Input
                  id="state"
                  value={addressInfo.state}
                  onChange={(e) =>
                    setAddressInfo((prev) => ({
                      ...prev,
                      state: e.target.value,
                    }))
                  }
                  placeholder="State"
                  required
                />
              </div>
              <div>
                <Label htmlFor="zip_code">ZIP *</Label>
                <Input
                  id="zip_code"
                  value={addressInfo.zip_code}
                  onChange={(e) =>
                    setAddressInfo((prev) => ({
                      ...prev,
                      zip_code: e.target.value,
                    }))
                  }
                  placeholder="ZIP code"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone_number">Phone *</Label>
                <Input
                  id="phone_number"
                  value={formData.phone_number}
                  onChange={(e) =>
                    handleInputChange("phone_number", e.target.value)
                  }
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="contact@dealer.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="website_url">Website</Label>
                <Input
                  id="website_url"
                  value={formData.website_url}
                  onChange={(e) =>
                    handleInputChange("website_url", e.target.value)
                  }
                  placeholder="https://dealer.com"
                />
              </div>
              <div>
                <Label htmlFor="dealer_type">Dealer Type *</Label>
                <Select
                  value={formData.dealer_type}
                  onValueChange={(value: string) =>
                    handleInputChange("dealer_type", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getDealerTypeOptions().map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* Lenders */}
            <div>
              <Label>Lenders</Label>
              <div className="mt-2">
                <MultiSelect
                  options={presetLenders}
                  selected={formData.lenders}
                  onChange={handleLendersChange}
                  placeholder="Select lenders or type to add custom..."
                  className="w-full"
                />
              </div>
            </div>

            <Separator />

            {/* Additional Fields */}
            <div>
              <h3 className="font-semibold mb-4">Additional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="onboarding_date">Onboarding Date</Label>
                  <Input
                    id="onboarding_date"
                    type="date"
                    value={formData.onboarding_date}
                    onChange={(e) =>
                      handleInputChange("onboarding_date", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="last_visit_date">Last Visit Date</Label>
                  <Input
                    id="last_visit_date"
                    type="date"
                    value={formData.last_visit_date}
                    onChange={(e) =>
                      handleInputChange("last_visit_date", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="vep_code">VEP Code</Label>
                  <Input
                    id="vep_code"
                    value={formData.vep_code}
                    onChange={(e) =>
                      handleInputChange("vep_code", e.target.value)
                    }
                    placeholder="Enter VEP code"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="dealer_feedback">Dealer Feedback</Label>
                  <Textarea
                    id="dealer_feedback"
                    value={formData.dealer_feedback}
                    onChange={(e) =>
                      handleInputChange("dealer_feedback", e.target.value)
                    }
                    placeholder="Enter dealer feedback..."
                    rows={3}
                  />
                </div>
              </div>

              <Separator className="my-4" />

              {/* DLA Configuration */}
              <div>
                <h4 className="font-semibold mb-3">DLA Configuration</h4>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="uses_dla"
                    checked={formData.uses_dla}
                    onCheckedChange={(checked: boolean) =>
                      handleCheckboxChange("uses_dla", checked)
                    }
                  />
                  <Label htmlFor="uses_dla" className="font-medium">
                    Uses DLA
                  </Label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="gradient-primary text-white"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Saving..."
                  : dealer
                    ? "Update Dealer"
                    : "Create Dealer"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
